import { useState } from 'react';
import { supabase } from '../../../../lib/supabase';
import { Client } from '../../../../context/ClientContext';
import { Button, Input, SidePanel } from '../../../ui';

interface MediaConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  client?: Client;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export default function MediaConfigModal({ isOpen, onClose, client, onSuccess }: MediaConfigModalProps) {
  const [mediaDomain, setMediaDomain] = useState('');
  const [isSavingDomain, setIsSavingDomain] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);

  const handleSaveMediaDomain = async () => {
    if (!client?.id || !mediaDomain.trim()) {
      setDomainError('Please enter a valid domain');
      return;
    }

    // Strip http://, https://, and trailing slashes
    let cleanDomain = mediaDomain.trim();
    cleanDomain = cleanDomain.replace(/^https?:\/\//, '');
    cleanDomain = cleanDomain.replace(/\/+$/, '');

    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])*$/;
    if (!domainRegex.test(cleanDomain)) {
      setDomainError('Please enter a valid domain (e.g., media.clientdomain.com)');
      return;
    }

    setIsSavingDomain(true);
    setDomainError(null);

    try {
      const { error } = await supabase
        .from('clients')
        .update({ media_domain: cleanDomain })
        .eq('id', client.id);

      if (error) throw error;

      // Update local client state if we have a way to do it
      if (client) {
        client.media_domain = cleanDomain;
      }

      onClose();
      setMediaDomain('');
      onSuccess('Media domain configured successfully!');

      // Refresh the component to show the new configuration
      window.location.reload();
    } catch (err) {
      console.error('Error saving media domain:', err);
      setDomainError('Failed to save media domain. Please try again.');
    } finally {
      setIsSavingDomain(false);
    }
  };

  const handleClose = () => {
    onClose();
    setMediaDomain('');
    setDomainError(null);
  };

  return (
    <SidePanel
      open={isOpen}
      onClose={handleClose}
      width={440}
      eyebrow="Media"
      title="Configure Media Domain"
      footer={
        <>
          <Button icon="check" onClick={handleSaveMediaDomain} disabled={isSavingDomain || !mediaDomain.trim()}>
            {isSavingDomain ? 'Saving...' : 'Save Domain'}
          </Button>
          <Button variant="ghost" onClick={handleClose} disabled={isSavingDomain}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Enter the custom domain configured for this client&apos;s R2 bucket.
        </p>

        <div>
          <Input
            label="Media Domain"
            value={mediaDomain}
            onChange={(e) => {
              setMediaDomain(e.target.value);
              if (domainError) setDomainError(null);
            }}
            placeholder="media.clientdomain.com"
          />
          {domainError && (
            <p style={{ margin: '6px 0 0', fontSize: 'var(--text-xs)', color: 'var(--danger-400)' }}>{domainError}</p>
          )}
        </div>

        <div
          style={{
            padding: '12px 14px',
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Required Bucket Name</div>
          <code className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--accent-text)' }}>
            {client?.id}
          </code>
        </div>
      </div>
    </SidePanel>
  );
}
